/**
 * Blog Sidebar Component
 *
 * A comprehensive sidebar component for blog pages that provides two main features:
 * 1. Newsletter subscription with Google reCAPTCHA verification and Azure Blob Storage persistence
 * 2. Display of popular blog posts with navigation links
 */

'use client';

import React from 'react';
import NewsletterSubscription from '@/components/common/NewsletterSubscription';
import Link from 'next/link';
import Image from 'next/image';
import { Post } from '@/types/post';

/**
 * Props interface for the BlogSidebar component
 */
interface BlogSidebarProps {
  /** Array of popular blog posts to display in the sidebar */
  popularPosts: Post[];
}

/**
 * BlogSidebar component with newsletter subscription and popular posts display
 *
 * This component renders a sidebar containing:
 * - A newsletter subscription form with reCAPTCHA verification
 * - A list of popular blog posts with navigation links
 *
 * The component manages its own state for form inputs and handles
 * the complete subscription flow including validation, API calls,
 * and user feedback.
 */
export default function BlogSidebar({ popularPosts }: BlogSidebarProps) {
  return (
    <div className="flex flex-col gap-8">
      <NewsletterSubscription />

      <div
        className="bg-androia-gray-50 rounded-3xl border border-androia-gray-200 p-6 animate-fade-in"
        style={{ animationDelay: '0.2s' }}
      >
        <h3 className="font-manrope font-semibold text-androia-text text-2xl text-center mb-8">
          Popular Posts
        </h3>

        <div className="flex flex-col gap-4">
          {popularPosts.map((post, index) => (
            <React.Fragment key={post.slug}>
              <Link href={`/blog/${post.slug}`} className="flex items-start gap-3 group">
                <Image
                  src={post.image}
                  alt={post.title}
                  width={56}
                  height={56}
                  className="object-cover rounded flex-shrink-0"
                />

                <div className="flex flex-col gap-2 flex-1">
                  <h4 className="font-manrope font-bold text-androia-text text-sm leading-tight group-hover:text-androia-blue transition-colors">
                    {post.title}
                  </h4>

                  <div className="flex items-center justify-between text-xs">
                    <span className="font-manrope font-semibold text-androia-gray-500">
                      {post.author.name}
                    </span>
                    <span className="font-manrope font-medium text-androia-gray-500">
                      {new Date(post.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                </div>
              </Link>
              {index < popularPosts.length - 1 && <div className="h-px bg-androia-gray-200" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
